import React, { useState } from 'react';
import * as Styled from './style';
import StepOne from './steps/StepOne';
import StepTwo from './steps/StepTwo';
import StepThree from './steps/StepThree';
import StepFour from './steps/StepFour';
import StepFive from './steps/StepFive';
import StepSix from './steps/StepSix';
import StepSeven from './steps/StepSeven';
import StepFinal from './steps/StepFinal';

type BusinessHours = {
  [key: string]: {
    active: boolean;
    from: string;
    to: string;
  };
};

type OfficeLocation = string;

type CommonQuestion = {
  question: string;
  answer: string;
};

type FormData = {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  emailForClients: string;
  cellPhone: string;
  businessHours: BusinessHours;
  engagementMethod: string;
  consultationProcess: string;
  officeLocations: OfficeLocation[];
  attorneys: string[];
  lawTypes: string;
  serviceAreas: string;
  excludedCases: string;
  allowScheduling: string;
  billingDescription: string;
  paymentPlans: string;
  commonQuestions: CommonQuestion[];
  caseTypes: string;
  caseDetails: string;
  formId: string;
};

type FormErrors = {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  emailForClients?: string;
  cellPhone?: string;
  engagementMethod?: string;
  consultationProcess?: string;
  officeLocations?: string[];
  attorneys?: string[];
  lawTypes?: string;
  serviceAreas?: string;
  excludedCases?: string;
  allowScheduling?: string;
  billingDescription?: string;
  paymentPlans?: string;
  commonQuestions?: string[];
  caseTypes?: string;
  caseDetails?: string;
  submit?: string;
};
const initialBusinessHours: BusinessHours = {
  MON: { active: false, from: '08:00', to: '17:00' },
  TUE: { active: false, from: '08:00', to: '17:00' },
  WED: { active: false, from: '08:00', to: '17:00' },
  THU: { active: false, from: '08:00', to: '17:00' },
  FRI: { active: false, from: '08:00', to: '17:00' },
  SAT: { active: false, from: '08:00', to: '17:00' },
  SUN: { active: false, from: '08:00', to: '17:00' },
};
const initialFormData: FormData = {
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  emailForClients: '',
  cellPhone: '',
  businessHours: initialBusinessHours,
  engagementMethod: '',
  consultationProcess: '',
  officeLocations: ['', ''],
  attorneys: ['', '', ''],
  lawTypes: '',
  serviceAreas: '',
  excludedCases: '',
  allowScheduling: '',
  billingDescription: '',
  paymentPlans: '',
  commonQuestions: [{ question: '', answer: '' }],
  caseTypes: '',
  caseDetails: '',
  formId: '41',
};

export default function ChatbotBusinessForm() {
  const [step, setStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  const [errors, setErrors] = useState({});
  const days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  const handleCheckboxChange = (day) => {
    setFormData((prev) => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          ...prev.businessHours[day],
          active: !prev.businessHours[day].active,
        },
      },
    }));
  };

  const handleTimeChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          ...prev.businessHours[day],
          [field]: value,
        },
      },
    }));
  };

  const addLocation = () => {
    setFormData((prev) => ({
      ...prev,
      officeLocations: [...prev.officeLocations, ''],
    }));
  };
  const formatPhoneNumber = (value) => {
    let cleaned = value.replace(/\D/g, '');

    if (cleaned.length > 10 && cleaned.startsWith('1')) {
      cleaned = cleaned.slice(1);
    }

    return cleaned.slice(0, 10);
  };
  const handleNext = () => {
    const newErrors: FormErrors = {};

    if (step === 2) {
      const firstNameError = validateField('firstName', formData.firstName);
      const lastNameError = validateField('lastName', formData.lastName);
      const companyNameError = validateField(
        'companyName',
        formData.companyName
      );
      const emailError = validateField('email', formData.email);
      const emailForClientsError = validateField(
        'emailForClients',
        formData.emailForClients
      );
      const phoneError = validateField('cellPhone', formData.cellPhone);

      if (firstNameError) newErrors.firstName = firstNameError;
      if (lastNameError) newErrors.lastName = lastNameError;
      if (companyNameError) newErrors.companyName = companyNameError;
      if (emailError) newErrors.email = emailError;
      if (emailForClientsError)
        newErrors.emailForClients = emailForClientsError;
      if (phoneError) newErrors.cellPhone = phoneError;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setStep((prev) => prev + 1);
  };
  const handleBack = () => setStep(step - 1);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    // Handle phone number formatting
    if (name === 'cellPhone') {
      const formattedPhone = formatPhoneNumber(value);

      setFormData((prev) => ({
        ...prev,
        [name]: formattedPhone,
      }));
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
    const error = validateField(name, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error || undefined,
    }));
  };
  const validateField = (name: string, value: string) => {
    let error = '';

    switch (name) {
      case 'firstName':
        if (!value.trim()) error = 'First Name is required.';
        break;
      case 'lastName':
        if (!value.trim()) error = 'Last Name is required.';
        break;
      case 'companyName':
        if (!value.trim()) error = 'Company Name is required.';
        break;
      case 'email':
        if (!value.trim()) error = 'Email is required.';
        else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/i.test(value))
          error = 'Invalid email format.';
        break;
      case 'emailForClients':
        if (value.trim() && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value))
          error = 'Invalid email format.';
        break;
      case 'cellPhone':
        if (value.trim() && !/^\d{10}$/.test(value))
          error = 'Phone number must be 10 digits.';
        break;
    }

    return error;
  };
  const handleBlur = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error,
      }));
    } else {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name as keyof FormErrors];
        return newErrors;
      });
    }
  };
  const validateForm = () => {
    const newErrors: FormErrors = {};

    Object.entries(formData).forEach(([key, value]) => {
      if (typeof value === 'string') {
        const error = validateField(key, value);
        if (error) {
          if (
            key === 'email' ||
            key === 'cellPhone' ||
            key === 'engagementMethod' ||
            key === 'consultationProcess' ||
            key === 'lawTypes' ||
            key === 'serviceAreas' ||
            key === 'excludedCases' ||
            key === 'allowScheduling' ||
            key === 'billingDescription' ||
            key === 'paymentPlans' ||
            key === 'caseTypes' ||
            key === 'caseDetails'
          ) {
            newErrors[key] = error;
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    function toISOStringTimeOnly(time: string): string {
      if (!time) return '';

      const [hourStr, minuteStr] = time.split(':');
      if (!hourStr || !minuteStr) return '';

      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);

      // Use a fixed date (e.g., 1970-01-01) to create a Date object
      const date = new Date(Date.UTC(1970, 0, 1, hour, minute));
      return date.toISOString(); // example: "1970-01-01T11:45:00.000Z"
    }

    const submissionData = {
      Name: {
        First: formData.firstName,
        Last: formData.lastName,
      },
      CompanyName: formData.companyName,
      Email: formData.email,
      EmailForClients: formData.emailForClients,
      Phone: formData.cellPhone,
      BusinessHours: Object.entries(formData.businessHours)
        .filter(([, data]) => data.active)
        .map(([day, data]) => ({
          Day: day,
          Active: true,
          From: toISOStringTimeOnly(data.from),
          To: toISOStringTimeOnly(data.to),
        })),
      EngagementMethod: formData.engagementMethod,
      ConsultationProcess: formData.consultationProcess,
      OfficeLocations: formData.officeLocations
        .filter((loc) => loc.trim() !== '')
        .map((loc) => ({ Location: loc })),
      Attorneys: formData.attorneys
        .filter((att) => att.trim() !== '')
        .map((att) => ({ Attorney: att })),
      LawTypes: formData.lawTypes,
      ServiceAreas: formData.serviceAreas,
      ExcludedCases: formData.excludedCases,
      AllowScheduling: formData.allowScheduling,
      BillingDescription: formData.billingDescription,
      PaymentPlans: formData.paymentPlans,
      CommonQuestions: formData.commonQuestions
        .filter((q) => q.question.trim() || q.answer.trim())
        .map((q) => ({
          Question: q.question,
          Answer: q.answer,
        })),
      CaseTypes: formData.caseTypes,
      CaseDetails: formData.caseDetails,
      formId: formData.formId,
    };

    try {
      const response = await fetch('/api/submitCognitoForm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();
      if (response.ok) {
        setFormData(initialFormData);
        setIsSubmitted(true);
      } else {
        setErrors({ submit: result.error });
      }
    } catch (err) {
      setErrors({
        submit: 'There was an error submitting the form. Please try again.',
      });
      console.error(err);
    }
  };

  return (
    <Styled.Container>
      <Styled.Form>
        {!isSubmitted ? (
          <>
            {step === 1 && <StepOne nextStep={handleNext} />}
            {step === 2 && (
              <StepTwo
                data={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleCheckboxChange={handleCheckboxChange}
                handleTimeChange={handleTimeChange}
                addLocation={addLocation}
                handleNext={handleNext}
                handleBack={handleBack}
              />
            )}
            {step === 3 && (
              <StepThree
                data={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleNext={handleNext}
                handleBack={handleBack}
              />
            )}
            {step === 4 && (
              <StepFour
                data={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleNext={handleNext}
                handleBack={handleBack}
              />
            )}
            {step === 5 && (
              <StepFive
                data={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleNext={handleNext}
                handleBack={handleBack}
              />
            )}
            {step === 6 && (
              <StepSix
                data={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleNext={handleNext}
                handleBack={handleBack}
              />
            )}
            {step === 7 && (
              <StepSeven
                data={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleSubmit={handleSubmit}
                handleBack={handleBack}
              />
            )}
          </>
        ) : (
          <StepFinal />
        )}
      </Styled.Form>
    </Styled.Container>
  );
}
